export type PdfTronQuad = {
  x1: number; y1: number;
  x2: number; y2: number;
  x3: number; y3: number;
  x4: number; y4: number;
};

export type StoredEntityBounds = {
  leftSide: number;
  rightSide: number;
  topSide: number;
  bottomSide: number;
};

export type PageInfo = {
  width: number;
  height: number;
};

/**
 * Convert stored bounds into PDFTron quad coordinates.
 *
 * Stored coordinates come from pdfplumber (top-left origin, Y increases downward).
 * PDFTron expects PDF page coordinates in points with bottom-left origin.
 *
 * Auto-detects the coordinate format:
 * - Normalized (0..1) vs page points
 * - Top-origin (topSide < bottomSide) vs bottom-origin (topSide > bottomSide)
 */
export function storedBoundsToPdfTronQuad(
  bounds: StoredEntityBounds,
  page: PageInfo,
): PdfTronQuad {
  const { width: pageWidth, height: pageHeight } = page;

  // Check if coordinates are normalized (0..1 range)
  const isProbablyNormalized =
    bounds.leftSide <= 1 && bounds.rightSide <= 1 &&
    bounds.topSide <= 1 && bounds.bottomSide <= 1;

  // Scale coordinates if they're normalized
  const xLeft = isProbablyNormalized ? bounds.leftSide * pageWidth : bounds.leftSide;
  const xRight = isProbablyNormalized ? bounds.rightSide * pageWidth : bounds.rightSide;

  const yStoredTop = isProbablyNormalized ? bounds.topSide * pageHeight : bounds.topSide;
  const yStoredBottom = isProbablyNormalized ? bounds.bottomSide * pageHeight : bounds.bottomSide;

  // Normalize X coordinates (may be swapped)
  const left = Math.min(xLeft, xRight);
  const right = Math.max(xLeft, xRight);

  // Detect coordinate origin and flip if needed:
  // Flip if topSide > bottomSide (reverse of the previous broken logic)
  const shouldFlip = yStoredTop > yStoredBottom;

  let yTop: number;
  let yBottom: number;

  if (shouldFlip) {
    // Flip Y-axis: convert to bottom-origin (PDFTron)
    yTop = pageHeight - yStoredTop;
    yBottom = pageHeight - yStoredBottom;
  } else {
    // Use as-is
    yTop = yStoredTop;
    yBottom = yStoredBottom;
  }

  // Always normalize Y ordering for quad (ensure consistent vertex order)
  const bottom = Math.min(yBottom, yTop);
  const top = Math.max(yBottom, yTop);

  // DEBUG: Remove after verifying highlights work correctly
  console.log('[pdfTronCoords]', {
    input: { L: bounds.leftSide, R: bounds.rightSide, T: bounds.topSide, B: bounds.bottomSide },
    page: { w: pageWidth, h: pageHeight },
    normalized: isProbablyNormalized,
    output: { left, right, top, bottom },
  });

  return {
    x1: left, y1: top,
    x2: right, y2: top,
    x3: right, y3: bottom,
    x4: left, y4: bottom,
  };
}